import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || '/';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate(redirectTo, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1758558531900-8e4e756be374?w=1200&q=80"
          alt="Wine cellar"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950/80 to-stone-950/40" />
        <div className="absolute bottom-12 left-12 right-12">
          <blockquote className="text-2xl font-serif text-stone-200 italic mb-4">
            "Wine is the only artwork you can drink."
          </blockquote>
          <p className="text-stone-400">— Luis Fernando Olaverri</p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-stone-950">
        <div className="w-full max-w-md">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-300 mb-12 transition-colors"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <img 
            src="https://customer-assets.emergentagent.com/job_winery-finder/artifacts/i4imw511_Vineyards%26Wine%20%281%29.png" 
            alt="VINEYARDS & WINE" 
            className="w-[200px] h-[120px] object-contain mb-8"
          />

          <h1 className="text-3xl font-serif text-stone-50 mb-2">Welcome Back</h1>
          <p className="text-stone-500 mb-8">Sign in to continue your wine journey</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-stone-500">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-transparent border-0 border-b border-stone-700 rounded-none px-0 py-4 focus:border-[#d4af37] focus:ring-0 placeholder:text-stone-600 text-lg"
                placeholder="your@email.com"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-stone-500">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-transparent border-0 border-b border-stone-700 rounded-none px-0 py-4 focus:border-[#d4af37] focus:ring-0 placeholder:text-stone-600 text-lg"
                placeholder="••••••••"
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none uppercase tracking-widest font-bold text-xs py-6 mt-8"
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <p className="mt-8 text-center text-stone-500">
            Don't have an account?{' '}
            <Link
              to="/register"
              className="text-[#d4af37] hover:underline"
              data-testid="register-link"
            >
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
